import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { WebsocketService } from '@shared/services/websocket/websocket.service'
import {
  IWsMessage,
  IWsMessageHelpDeskMessageDeleted,
  IWsMessageHelpDeskMessageEdited,
  IWsMessageHelpDeskMessageNew,
  WsMessageEventTypes,
} from '@shared/services/websocket/websocket.interface'
import { GetClientChatMessagesQuery } from '@app/support-module/core/queries/get-client-chat-messages.query'
import { IChatMessage } from '@app/support-module/core/types'

/**
 * Держит ленту сообщений чата клиентской стороны (виджет).
 * Фильтрует `help_desk:message_*` по `chat_id`; `organization_id` нужен только для REST-запросов.
 *
 * Scope: инстанс на компонент виджета (`providers: [SupportWidgetMessagesService]`).
 */
@Injectable()
export class SupportWidgetMessagesService implements OnDestroy {
  private readonly destroyed$ = new Subject<void>()
  private readonly _messages$ = new BehaviorSubject<IChatMessage[]>([])
  private readonly _loading$ = new BehaviorSubject<boolean>(false)

  private currentChatId: number | null = null

  constructor(
    private readonly getMessagesQuery: GetClientChatMessagesQuery,
    private readonly websocketService: WebsocketService
  ) {
    this.subscribeToWs()
  }

  ngOnDestroy(): void {
    this.destroyed$.next()
    this.destroyed$.complete()
    this._messages$.complete()
    this._loading$.complete()
  }

  get messages$(): Observable<IChatMessage[]> {
    return this._messages$.asObservable()
  }

  get loading$(): Observable<boolean> {
    return this._loading$.asObservable()
  }

  /** Устанавливает активный чат и грузит первую страницу. */
  load(organizationId: number, chatId: number, perPage = 50): void {
    this.currentChatId = chatId
    this._messages$.next([])
    this._loading$.next(true)

    this.getMessagesQuery
      .call(organizationId, 1, perPage)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const asc = [...response.data].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          )
          this._messages$.next(asc)
          this._loading$.next(false)
        },
        error: (err) => {
          console.error('[support-widget-messages] failed to load', err)
          this._loading$.next(false)
        },
      })
  }

  /** Добавляет сообщение, отправленное самим клиентом (оптимистично, до WS-отклика). */
  appendLocal(message: IChatMessage): void {
    this.append(message)
  }

  private subscribeToWs(): void {
    this.websocketService
      .on(WsMessageEventTypes.HELP_DESK_MESSAGE_NEW)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((msg: IWsMessage<IWsMessageHelpDeskMessageNew>) => {
        if (msg.data.chat_id !== this.currentChatId) return
        this.append(msg.data.message)
      })

    this.websocketService
      .on(WsMessageEventTypes.HELP_DESK_MESSAGE_EDITED)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((msg: IWsMessage<IWsMessageHelpDeskMessageEdited>) => {
        if (msg.data.chat_id !== this.currentChatId) return
        this.update(msg.data.message)
      })

    this.websocketService
      .on(WsMessageEventTypes.HELP_DESK_MESSAGE_DELETED)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((msg: IWsMessage<IWsMessageHelpDeskMessageDeleted>) => {
        if (msg.data.chat_id !== this.currentChatId) return
        this.markDeleted(msg.data.message_id)
      })
  }

  private append(message: IChatMessage): void {
    const current = this._messages$.value
    if (current.some((m) => m.id === message.id)) {
      this.update(message)
      return
    }
    this._messages$.next([...current, message])
  }

  private update(message: IChatMessage): void {
    const current = this._messages$.value
    const idx = current.findIndex((m) => m.id === message.id)
    if (idx < 0) return
    const next = [...current]
    next[idx] = message
    this._messages$.next(next)
  }

  private markDeleted(messageId: number): void {
    const current = this._messages$.value
    const idx = current.findIndex((m) => m.id === messageId)
    if (idx < 0) return
    const next = [...current]
    next[idx] = { ...next[idx], deleted_at: new Date().toISOString() }
    this._messages$.next(next)
  }
}
